import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useWaSecurityService from 'src/api/wa/service/WaSecurityService';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useWaErrorStore } from 'src/api/wa/store/WaErrorStore';
import { MissingParameterError } from 'src/api/common/error/HttpError';
import { FormRow } from 'src/ui/wards/common/form/FormRow';
import Input from 'src/ui/wards/common/input/Input';
import Button from 'src/ui/wards/common/button/Button';

function WaLogin() {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showMissingUsername, setShowMissingUsername] = useState(false);
  const [showMissingPassword, setShowMissingPassword] = useState(false);
  const [underAuthentication, setUnderAuthentication] = useState(false);
  const [showAuthenticationError, setAuthenticationError] = useState(false);
  const { errors } = useWaErrorStore();
  const { waLoginUser, waGetUser } = useWaSecurityService();

  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();

    const isUsernameEmpty = username.length === 0;
    const isPasswordEmpty = password.length === 0;

    setShowMissingUsername(isUsernameEmpty);
    setShowMissingPassword(isPasswordEmpty);
    if (isUsernameEmpty || isPasswordEmpty) {
      return;
    }

    setAuthenticationError(false);
    setUnderAuthentication(true);

    try {
      await waLoginUser(username, password);
      await waGetUser();
    } catch (err) {
      console.log('Login err', err);
    } finally {
      setUnderAuthentication(false);
    }
  };

  useEffect(() => {
    if (underAuthentication) {
      const hasLoginError = errors.some((error) => error instanceof MissingParameterError);
      if (hasLoginError) {
        setAuthenticationError(true);
      }
    }
  }, [errors, underAuthentication]);

  return (
    <div className="flex h-screen flex-col items-center justify-center bg-gray-100">
      <div className="w-full max-w-sm rounded bg-white p-8 shadow-md">
        <form onSubmit={handleSubmit}>
          <h2 className="mb-6 text-center text-2xl font-bold">{t('wards.common.title')}</h2>

          <div className="space-y-4">
            <FormRow
              layout="vertical"
              showError={showMissingUsername}
              errorMessage={t('wards.login.missingUsername')}
            >
              <Input
                type="text"
                className="w-full rounded border p-2"
                value={username}
                placeholder={t('wards.login.ldapId')}
                onChange={(event) => {
                  setShowMissingUsername(false);

                  setUsername(event.target.value);
                }}
              />
            </FormRow>

            <FormRow
              layout="vertical"
              showError={showMissingPassword}
              errorMessage={t('wards.login.missingPassword')}
            >
              <Input
                type="password"
                className="w-full rounded border p-2"
                value={password}
                placeholder={t('wards.login.password')}
                onChange={(event) => {
                  setShowMissingPassword(false);

                  setPassword(event.target.value);
                }}
              />
            </FormRow>

            {showAuthenticationError && (
              <div className="flex items-center justify-center text-sm text-red-500">
                <ExclamationCircleIcon className="mr-1 size-5" />

                <span>{t('wards.login.error')}</span>
              </div>
            )}
          </div>

          <div className="mt-6">
            <Button type="submit" title={t('wards.login.login')} className="w-full" />
          </div>
        </form>
      </div>
    </div>
  );
}

export default WaLogin;
